/*
    This service obtains dynamic POI data from our own backend REST API service.
    Currently it drives dynamic data for Security Wait Times and Parking Status.

    Configuration Properties Recognized:
      urlBase : to override the REST url base. i.e. for alpha use 'https://rest-alpha.locuslabs.com/v1'. You can also reference
          a local file, such as './testDynamicPois.json'

    Values defined by 'parking':
      lotName: Name of the lot for this POI, such as 'East'
      lotStatus: returns either 'Closed' or 'Open'
      timeIsReal: If true, the time here is live and valid, else its a default fallback
      rateDay: Daily Rate expressed in English: '$17 per day'
      rateHour: Hourly Rate expressed in English: '$5 per hour'
      timeToTerminal1: Time in walking or by shuttle to Terminal 1 : 'Shuttle 5-7'
      timeToTerminal2: Time in nwalking or by shuttle to Terminal 2 : 'Walking 5-10'
      lastUpdated: timestamp of last update (as sent by server)

    Values defined by 'security'
      queueTime: Estimated Time in queue (in minutes) : i.e. 45
      isTemporarilyClosed: If true, this line is closed - else it is open
      timeIsReal: If true, the time here is live and valid, else its a default fallback
      lastUpdated: timestamp of last update (as sent by server)
 */

import Zousan from 'zousan';

import type { PluginConfig } from '../config-schema.ts';
import * as Processors from './processors.ts';

let REFRESH_FREQUENCY = 1000 * 30; // every 30 seconds
const MARKETPLACE_URL_BASE = 'https://marketplace.locuslabs.com';

const MAX_FETCH_FAILS = 3; // 3 strikes and you're out!

const ACCOUNT_ID_HEADER = 'x-account-id';
const isObject = value => typeof value === 'object' && value !== null && !Array.isArray(value);

function create(app, config: PluginConfig & { _overrideRefreshFrequency?: number } = {}) {
  const state = {
    dynamicDataNotPending: new Zousan(),
  };
  const T = app.gt();

  /**
   * Returns security checkpoint POIs that have dynamic security data.
   *
   * @param {{ isOpen?: boolean }} [options] Optional open-state filter. Omit to return all checkpoints,
   * set to true for open checkpoints, or false for temporarily closed checkpoints.
   * @returns {Promise<Array<{id: number, name: string, queueTime?: number, isTemporarilyClosed?: boolean, timeIsReal?: boolean, lastUpdated?: string}>>}
   */
  const getSecurityWaitTimes = async ({ isOpen } = {}) => {
    const pois = await app.bus.get('poi/getAll');
    return Object.values(pois)
      .filter(
        poi =>
          poi.category === 'security.checkpoint' &&
          poi?.dynamicData?.security &&
          isObject(poi?.dynamicData?.security) &&
          (isOpen === undefined ? true : poi?.dynamicData?.security?.isTemporarilyClosed !== isOpen),
      )
      .map(poi => ({
        id: poi.poiId,
        name: poi.name,
        ...poi.dynamicData.security,
      }));
  };

  // used in testing
  if (config._overrideRefreshFrequency) {
    REFRESH_FREQUENCY = config._overrideRefreshFrequency;
  }

  // Tracks the last-emitted name override per POI to detect changes across poll cycles.
  // poiId (string) -> string | null
  const previousNameOverrides = new Map();

  // by returning this dynamicDataLoaded promise, we hold sdkReady event until this is resolved
  app.bus.on('system/readywhenyouare', () => state.dynamicDataNotPending);
  app.bus.on('dynamicPois/getSecurityWaitTimes', getSecurityWaitTimes);

  const init = async () => {
    const [accountId, venueId] = await Promise.all([
      app.bus.get('venueData/getAccountId'),
      app.bus.get('venueData/getVenueId'),
    ]);

    const dynamicPoisUrl = `${MARKETPLACE_URL_BASE}/venueId/${venueId}/dynamic-poi`;

    let fetchFailuresCount = 0;
    let fetchSuccessCount = 0;
    let fetchIntervalHandle;
    const updateFromAPI = async () => {
      const response = await fetch(dynamicPoisUrl, {
        headers: { [ACCOUNT_ID_HEADER]: accountId },
      });
      if (response.ok) {
        // 404 if customer does not have dynamic POIs
        return response
          .json()
          .then(({ data }) => processDynamicPois(data))
          .then(() => {
            fetchSuccessCount++;
          })
          .catch(console.error);
      } else {
        console.warn('dynamicPois: fetch response status not ok', response);
        fetchFailuresCount++;
        // Don't keep trying after multiple fails unless past success is at least 50%
        if (fetchFailuresCount >= MAX_FETCH_FAILS && fetchFailuresCount > fetchSuccessCount) {
          clearInterval(fetchIntervalHandle);
        }
      }
    };

    await updateFromAPI()
      .then(() => {
        fetchIntervalHandle = setInterval(updateFromAPI, REFRESH_FREQUENCY);
        if (typeof fetchIntervalHandle.unref === 'function') {
          fetchIntervalHandle.unref();
        } // Node only - do not keep-alive Lambda or other short-term environments
      })
      .finally(() => state.dynamicDataNotPending.resolve(true)); // even in case of failure, we don't want to hold up the UI

    return () => clearInterval(fetchIntervalHandle);
  };

  async function processDynamicPois(pois) {
    processParkingPOIS(pois);
    processOpenClosedPois(pois);
    processSecurityWaitTimes(pois);
    processDynamicRouting(pois);
    processDynamicAttributes(pois);
  }

  /**
   * Extracts `dynamicData.poiAttributes.name` overrides from each POI in the API response and emits
   * `poi/setCoreAttributes` and `map/mutateFeature` for any changes. A `null` name signals a revert to the original value.
   * Only emits when a value actually changes to avoid unnecessary downstream updates.
   *
   * @param {Object} poiDataMap - The `data` map from the /dynamic-poi API response
   */
  async function processDynamicAttributes(poiDataMap) {
    const updates = {};

    for (const [poiId, poiData] of Object.entries(poiDataMap)) {
      const name = poiData?.dynamicData?.poiAttributes?.name ?? null;
      const previous = previousNameOverrides.get(poiId); // undefined if never tracked

      if (previous === undefined) {
        // First time seeing this POI: only track and emit if there's an actual name override
        if (name !== null) {
          updates[poiId] = { name };
          previousNameOverrides.set(poiId, name);
        }
      } else if (previous !== name) {
        // Value changed (including override → null revert)
        updates[poiId] = { name };
        previousNameOverrides.set(poiId, name);
      }
    }

    // Emit null for any POIs that were previously overridden but are no longer in the response
    for (const [poiId, prevName] of previousNameOverrides.entries()) {
      if (!(poiId in poiDataMap) && prevName !== null) {
        updates[poiId] = { name: null };
        previousNameOverrides.set(poiId, null);
      }
    }

    if (Object.keys(updates).length > 0) {
      app.bus.send('poi/setCoreAttributes', updates);
      const originalNames = await getPoiNameMap(Object.keys(updates), poi => poi.staticName ?? poi.name);
      app.bus.send('map/mutateFeature', {
        functor: Processors.mutatePoiName(T, updates, originalNames),
      });
    }
  }

  function processParkingPOIS(poiMap) {
    const idValuesMap = Processors.processParkingPOIS(poiMap);
    app.bus.send('poi/setDynamicData', { plugin: 'parking', idValuesMap });
  }

  const getPoiNameMap = async (
    idArray: string[],
    selector: (poi: { name: string; staticName?: string; [key: string]: unknown }) => string = poi => poi.name,
  ): Promise<Record<string, string>> => {
    const nameMap: Record<string, string> = {};
    for (const poiId of idArray) {
      const poi = await app.bus.get('poi/getById', { id: poiId });
      if (poi) {
        nameMap[poiId] = selector(poi);
      }
    }
    return nameMap;
  };

  /*
          API response: https://gitlab.com/locuslabs/core-data-team/json-schemas/-/blob/develop/src/api-marketplace/dynamic-queue-data.json
         */
  async function processSecurityWaitTimes(poiMap) {
    const idValuesMap = Processors.processSecurityWaitTimes(poiMap);

    app.bus.send('poi/setDynamicData', { plugin: 'security', idValuesMap });

    const labels = await getPoiNameMap(Object.keys(idValuesMap)); // get a map of labels for the POIs - used in wait time labeling

    app.bus.send('map/mutateFeature', {
      functor: Processors.mutateSecurityCheckpointLabel(T, idValuesMap, labels),
    });
  }

  function processOpenClosedPois(poiMap) {
    const idValuesMap = Processors.processOpenClosedPois(poiMap);
    app.bus.send('poi/setDynamicData', {
      plugin: 'open-closed-status',
      idValuesMap,
    });
  }

  async function processDynamicRouting(poiMap) {
    const idValuesMap = await Processors.processRoutingPois(poiMap);
    app.bus.send('poi/setDynamicRouting', { plugin: 'routing', idValuesMap });
  }

  return {
    init,
  };
}

export { create };
